import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { formatCredits } from 'features/usage/components/usage-credits';
import { useCreditSpend, useCreditSummary } from '../hooks/use-credits';
import { BuyCreditsDialog } from './buy-credits-dialog';

/** The organization's top-up credits on Plans & Billing: balance, what was spent, and buying more. */
export const BillingCreditsCard = () => {
  const { t } = useTranslation();
  const [isBuying, setIsBuying] = useState(false);
  const { data: summary } = useCreditSummary();
  const { data: spend } = useCreditSpend(!!summary?.enabled);

  // `enabled` is false until the first top-up creates the wallet, so it would hide the only way to buy.
  if (!summary) return null;

  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none lg:col-span-2">
      <CardContent className="flex h-full flex-col gap-4 p-5">
        <h2 className="text-sm font-medium text-foreground">{t('CREDITS', 'Credits')}</h2>
        <div>
          <p className="text-2xl font-medium leading-heading text-foreground sm:text-3xl sm:leading-heading">
            {formatCredits(summary.balanceCredits)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('CREDITS_AVAILABLE_TO_WORKSPACE', 'Available to your workspace')}
          </p>
        </div>

        {spend && (
          <div className="flex items-center justify-between gap-3 border-t border-border pt-4 text-sm">
            <span className="text-muted-foreground">{t('CREDITS_USED_SO_FAR', 'Used so far')}</span>
            <span className="text-foreground">{formatCredits(spend.lifetimeSpentCredits)}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {t('CREDITS_NOT_PART_OF_SEAT_BILL', 'Paid once, not part of your seat bill. Credits never expire.')}
        </p>

        {summary.canTopUp && (
          <div className="mt-auto">
            <Button size="sm" className="rounded-full" onClick={() => setIsBuying(true)}>
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('BUY_CREDITS', 'Buy credits')}
            </Button>
          </div>
        )}
      </CardContent>

      {/* Mounted only while open, so each purchase starts from the first pack. */}
      {isBuying && <BuyCreditsDialog summary={summary} onClose={() => setIsBuying(false)} />}
    </Card>
  );
};
